import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useWebSocket } from "@/hooks/use-websocket";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { 
  ArrowLeft, Calendar, PoundSterling, MapPin, Clock, Users, Mail, Phone, 
  Building, FileText, Music, Edit, Trash2, CreditCard, FileSignature,
  Link, MoreVertical, Copy, Check, Shield, ShieldOff
} from "lucide-react";
import { format } from "date-fns";
import type { Booking, ExternalEvent, Venue, ActWithParticipants } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { BookingFormModal } from "@/components/bookings/booking-form-modal";
import { FeeComparisonIndicator } from "@/components/bookings/fee-comparison-indicator";
import { useToast } from "@/hooks/use-toast";

type BookingWithDetails = Booking & {
  externalEvent: ExternalEvent & { venue?: Venue };
  act: ActWithParticipants;
};

export default function BookingSummary() {
  const { orgId, bookingId } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationUrl, setConfirmationUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  // Enable WebSocket for real-time updates
  const { isConnected } = useWebSocket();
  
  // Debug WebSocket connection
  useEffect(() => {
    console.log('[BookingSummary] WebSocket connected:', isConnected);
    console.log('[BookingSummary] Watching booking ID:', bookingId);
  }, [isConnected, bookingId]);

  // Check permissions
  useEffect(() => {
    if (!orgId || !bookingId) {
      setLocation("/");
      return;
    }

    const organisationId = parseInt(orgId || "0");
    const hasAccess = user?.role === 'god' || 
      user?.organisationUsers?.some(ou => 
        ou.organisation.id === organisationId && 
        (ou.role === 'owner' || ou.role === 'operator')
      );

    if (!hasAccess) {
      setLocation("/");
    }
  }, [orgId, bookingId, user, setLocation]);

  // Fetch booking details
  const { data: booking, isLoading } = useQuery<BookingWithDetails>({
    queryKey: [`/api/bookings/${bookingId}`],
    queryFn: async () => {
      console.log(`[BookingSummary] Fetching booking ${bookingId}`);
      const response = await fetch(`/api/bookings/${bookingId}`);
      if (!response.ok) throw new Error("Failed to fetch booking");
      const data = await response.json();
      console.log(`[BookingSummary] Fetched booking ${bookingId}:`, {
        status: data.status,
        confirmationEnabled: data.confirmationEnabled,
        confirmationRespondedAt: data.confirmationRespondedAt
      });
      return data;
    },
    enabled: !!bookingId,
    refetchInterval: false,
    staleTime: 0, // Always consider data stale to ensure updates are fetched
  });

  // Delete booking mutation
  const deleteBookingMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/bookings/${bookingId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error("Failed to delete booking");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/bookings`] });
      toast({
        title: "Booking Deleted",
        description: "The booking has been deleted successfully.",
      });
      setLocation(`/organisations/${orgId}/acts`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Generate confirmation URL mutation
  const generateConfirmationUrlMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/bookings/${bookingId}/confirmation-url`, {
        method: 'POST',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to generate confirmation URL");
      }
      return response.json();
    },
    onSuccess: (data) => {
      setConfirmationUrl(data.fullUrl);
      setShowConfirmationModal(true);
      // Force refetch to update the UI with the new confirmation URL
      queryClient.invalidateQueries({ queryKey: [`/api/bookings/${bookingId}`] });
      queryClient.refetchQueries({ queryKey: [`/api/bookings/${bookingId}`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle confirmation enabled mutation
  const toggleConfirmationEnabledMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await fetch(`/api/bookings/${bookingId}/confirmation-enabled`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!response.ok) throw new Error("Failed to update confirmation status");
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/bookings/${bookingId}`] });
      toast({
        title: data.enabled ? "Link Enabled" : "Link Disabled",
        description: data.enabled 
          ? "The confirmation link has been enabled." 
          : "The confirmation link has been disabled.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateDepositPaidMutation = useMutation({
    mutationFn: async (depositPaid: boolean) => {
      const response = await fetch(`/api/bookings/${bookingId}?organisationId=${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ depositPaid }),
      });
      if (!response.ok) throw new Error("Failed to update deposit status");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/bookings/${bookingId}`] });
      toast({
        title: "Deposit Status Updated",
        description: "The deposit payment status has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "completed":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800 border-green-200";
      case "partial":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "refunded":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const copyToClipboard = async () => {
    if (!confirmationUrl) return;
    try {
      await navigator.clipboard.writeText(confirmationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Copied!",
        description: "Confirmation URL copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <FileText className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
            <p className="text-slate-700">Loading booking details...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (!booking) {
    return (
      <PageBackground>
        <AppHeader />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <p className="text-slate-700">Booking not found</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  const actName = booking.act?.name || booking.act?.participants?.[0]?.participant.name || "Unknown Act";
  
  // Use the calculated commission amount from the database
  const commissionAmount = booking.commissionAmount ? booking.commissionAmount / 100 : 0;
  const netAmount = booking.fee ? (booking.fee / 100) - commissionAmount : 0;

  // Determine contact info for event
  const eventContact = booking.externalEvent?.useVenueContact ? booking.externalEvent.venue : booking.externalEvent;

  return (
    <PageBackground>
      <AppHeader backUrl={`/organisations/${orgId}/acts`} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">

          <div className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 drop-shadow-sm">
                  Booking Details
                </h1>
                <p className="text-slate-600 mt-1">
                  ID: #{booking.id}
                </p>
              </div>
              <div className="flex gap-2">
                <Badge className={`${getStatusColor(booking.status)} px-3 py-1`}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </Badge>
                {booking.paymentStatus && booking.paymentStatus !== 'pending' && (
                  <Badge className={`${getPaymentStatusColor(booking.paymentStatus)} px-3 py-1`}>
                    {booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1)}
                  </Badge>
                )}
              </div>
            </div>

            {/* Act Information */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Music className="h-5 w-5 text-purple-600" />
                Act Information
              </h2>
              <Card 
                className="bg-white/30 backdrop-blur-sm border-white/20 hover:bg-white/40 transition-all cursor-pointer"
                onClick={() => setLocation(`/organisations/${orgId}/acts/${booking.actId}/summary`)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-slate-900">{actName}</p>
                      {booking.act?.participants && booking.act.participants.length > 0 && booking.act.name && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {booking.act.participants.map(ap => (
                            <Badge key={ap.participantId} variant="outline" className="text-xs">
                              {ap.participant.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {booking.act?.genre && booking.act.genre.length > 0 && (
                        <p className="text-sm text-slate-600 mt-1">
                          {booking.act.genre.join(", ")}
                        </p>
                      )}
                    </div>
                    {booking.act?.actType && (
                      <Badge variant="secondary">
                        {booking.act.actType.charAt(0).toUpperCase() + booking.act.actType.slice(1)}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Event Information */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Event Information
              </h2>
              <Card 
                className="bg-white/30 backdrop-blur-sm border-white/20 hover:bg-white/40 transition-all cursor-pointer"
                onClick={() => setLocation(`/organisations/${orgId}/events/${booking.externalEventId}/summary`)}
              >
                <CardContent className="p-4">
                  <p className="font-semibold text-slate-900 mb-2">{booking.externalEvent?.name}</p>
                  <div className="space-y-2 text-sm text-slate-600">
                    {booking.externalEvent?.venue && (
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        <span>{booking.externalEvent.venue.name}</span>
                      </div>
                    )}
                    {booking.externalEvent?.venue?.address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>
                          {booking.externalEvent.venue.address}
                          {booking.externalEvent.venue.city && `, ${booking.externalEvent.venue.city}`}
                          {booking.externalEvent.venue.state && `, ${booking.externalEvent.venue.state}`}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {booking.externalEvent?.date && 
                          format(new Date(booking.externalEvent.date), "PPP")}
                      </span>
                    </div>
                    {eventContact && (eventContact.contactName || eventContact.contactEmail || eventContact.contactPhone) && (
                      <div className="pt-2 border-t border-white/20">
                        <p className="font-medium mb-1">Event Contact:</p>
                        {eventContact.contactName && (
                          <div className="flex items-center gap-2">
                            <Users className="h-3 w-3" />
                            <span>{eventContact.contactName}</span>
                          </div>
                        )}
                        {eventContact.contactEmail && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            <a href={`mailto:${eventContact.contactEmail}`} className="text-blue-600 hover:underline">
                              {eventContact.contactEmail}
                            </a>
                          </div>
                        )}
                        {eventContact.contactPhone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            <a href={`tel:${eventContact.contactPhone}`} className="text-blue-600 hover:underline">
                              {eventContact.contactPhone}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Performance Details */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-green-600" />
                Performance Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/30 rounded-lg p-3">
                  <p className="text-xs text-slate-600 mb-1">Performance Time</p>
                  <p className="font-medium">
                    {booking.performanceTime 
                      ? format(new Date(booking.performanceTime), "p")
                      : "Not scheduled"}
                  </p>
                </div>
                <div className="bg-white/30 rounded-lg p-3">
                  <p className="text-xs text-slate-600 mb-1">Duration</p>
                  <p className="font-medium">
                    {booking.durationMinutes 
                      ? `${booking.durationMinutes} minutes`
                      : "Not specified"}
                  </p>
                </div>
              </div>
            </div>

            {/* Financial Details */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <PoundSterling className="h-5 w-5 text-green-600" />
                Financial Details
              </h2>
              <div className="bg-white/30 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Performance Fee</span>
                  <span className="font-semibold">
                    {booking.fee ? `£${(booking.fee / 100).toFixed(2)}` : "Not specified"}
                  </span>
                </div>
                <FeeComparisonIndicator
                  actualFee={booking.fee}
                  minimumFee={booking.act?.minimumFee}
                  preferredFee={booking.act?.preferredFee}
                />
                {booking.commissionType && booking.commissionValue && (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">
                        Commission ({booking.commissionType === 'percentage' 
                          ? `${(booking.commissionValue / 100).toFixed(1)}%`
                          : "Flat"})
                      </span>
                      <span>-£{commissionAmount.toFixed(2)}</span>
                    </div>
                    <Separator className="bg-white/20" />
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 font-medium">Net Amount</span>
                      <span className="font-bold text-lg">£{netAmount.toFixed(2)}</span>
                    </div>
                  </>
                )}
                
                {/* Deposit Section */}
                {booking.hasDeposit && (
                  <>
                    <Separator className="bg-white/20" />
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Deposit Required</span>
                        <span className="font-semibold">
                          £{booking.depositAmount ? (booking.depositAmount / 100).toFixed(2) : "0.00"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="depositPaid" className="text-sm text-slate-600">
                          Deposit Paid
                        </Label>
                        <Switch
                          id="depositPaid"
                          checked={booking.depositPaid || false}
                          onCheckedChange={(checked) => updateDepositPaidMutation.mutate(checked)}
                          disabled={updateDepositPaidMutation.isPending}
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <Separator className="bg-white/20" />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600 mb-1">Payment Status</p>
                    <Badge className={getPaymentStatusColor(booking.paymentStatus)}>
                      {booking.paymentStatus}
                    </Badge>
                  </div>
                  {booking.invoiceNumber && (
                    <div>
                      <p className="text-slate-600 mb-1">Invoice #</p>
                      <p className="font-medium">{booking.invoiceNumber}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-indigo-600" />
                Additional Information
              </h2>
              <div className="bg-white/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-600">Contract Status</span>
                  <Badge className={booking.contractSigned ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                    {booking.contractSigned ? "Signed" : "Not Signed"}
                  </Badge>
                </div>
                {booking.notes && (
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Notes</p>
                    <p className="text-sm italic">{booking.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => setShowEditModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Booking
              </Button>
              
              {/* Confirmation URL Actions */}
              {booking?.confirmationUrl ? (
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setConfirmationUrl(`${window.location.origin}/booking-confirmation/${booking.confirmationUrl}`);
                      setShowConfirmationModal(true);
                    }}
                    variant="outline"
                    className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
                  >
                    <Link className="h-4 w-4 mr-2" />
                    View Confirmation URL
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={() => toggleConfirmationEnabledMutation.mutate(!booking.confirmationEnabled)}
                      >
                        {booking.confirmationEnabled ? (
                          <>
                            <ShieldOff className="h-4 w-4 mr-2" />
                            Disable Link
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4 mr-2" />
                            Enable Link
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                booking?.status === 'pending' && (
                  <Button
                    onClick={() => generateConfirmationUrlMutation.mutate()}
                    variant="outline"
                    className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
                  >
                    <Link className="h-4 w-4 mr-2" />
                    Get Confirmation URL
                  </Button>
                )
              )}
              
              <Button
                onClick={() => {
                  if (confirm("Are you sure you want to delete this booking?")) {
                    deleteBookingMutation.mutate();
                  }
                }}
                variant="destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Booking
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Booking Modal */}
      <BookingFormModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        organisationId={parseInt(orgId || "0")}
        booking={booking}
      />
      
      {/* Confirmation URL Modal */}
      <Dialog open={showConfirmationModal} onOpenChange={setShowConfirmationModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-purple-600" />
              Booking Confirmation URL
            </DialogTitle>
            <DialogDescription>
              Share this URL with the act to allow them to confirm or reject the booking.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {booking?.confirmationEnabled === false && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <ShieldOff className="h-4 w-4" />
                  This confirmation link is currently disabled
                </p>
              </div>
            )}
            
            {booking?.confirmationRespondedAt && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  The act has already responded to this booking.
                  <br />
                  Status: <Badge className="ml-1">{booking.status}</Badge>
                </p>
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <div className="grid flex-1 gap-2">
                <Label htmlFor="confirmation-url" className="sr-only">
                  Confirmation URL
                </Label>
                <Input
                  id="confirmation-url"
                  value={confirmationUrl || ''}
                  readOnly
                  className="font-mono text-xs"
                />
              </div>
              <Button
                type="button"
                size="sm"
                className="px-3"
                onClick={copyToClipboard}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    <span className="sr-only">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span className="sr-only">Copy</span>
                  </>
                )}
              </Button>
            </div>
            
            <div className="text-sm text-slate-600 space-y-1">
              <p>• The act can use this link to review and confirm the booking details</p>
              <p>• The link will be disabled after the act responds</p>
              <p>• You can manually disable the link using the menu button</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageBackground>
  );
}